import { useEffect, useRef, useState } from 'react';

const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 400;

/**
 * Tracks the load state of a CDN image.
 *
 * - For already-cached images (`img.complete && naturalWidth > 0`) `loaded` is
 *   set to `true` synchronously after mount, so neither the skeleton phase nor
 *   the fade-in transition play.
 * - `onLoad`  — pass to `<img onLoad>` to mark the image ready.
 * - `onError` — pass to `<img onError>`. A transient failure is retried up to
 *   `MAX_RETRIES` times (imperatively via `ref`, with a cache-busting query so
 *   the browser re-fetches instead of reusing its negative cache); only after
 *   the retries are exhausted is `failed` set so the skeleton clears and the
 *   fallback shows.
 */
export function useImageLoad(src: string | undefined) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const retriesRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    retriesRef.current = 0;
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!src) {
      setLoaded(false);
      setFailed(false);
      return;
    }
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) {
      // Already in browser cache — skip skeleton/fade-in.
      setLoaded(true);
      setFailed(false);
    } else {
      setLoaded(false);
      setFailed(false);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [src]);

  return {
    ref,
    loaded,
    failed,
    onLoad: () => {
      setLoaded(true);
      setFailed(false);
    },
    onError: () => {
      if (src && retriesRef.current < MAX_RETRIES) {
        const attempt = (retriesRef.current += 1);
        timerRef.current = setTimeout(() => {
          const img = ref.current;
          if (!img) return;
          const separator = src.includes('?') ? '&' : '?';
          img.src = `${src}${separator}retry=${attempt}`;
        }, RETRY_BASE_DELAY_MS * attempt);
        return;
      }
      setFailed(true);
      setLoaded(false);
    },
  };
}
